import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { BookingWithClient, listBookings } from "@/lib/bookingsApi";
import {
  CATEGORY_LABELS,
  formatDisplayDate,
  formatTime,
  paymentStatusClass,
  isOverdue,
} from "@/lib/bookingLabels";
import {
  WEEKDAY_LABELS,
  addDays,
  addMonths,
  formatMonthYear,
  formatWeekRange,
  isSameDay,
  isSameMonth,
  monthGrid,
  startOfWeek,
  toISODate,
  weekDays,
} from "@/lib/calendarUtils";
import { formatGbpFromPence } from "@/types/database";
import { isSupabaseConfigured } from "@/lib/supabase";

type ViewMode = "month" | "week" | "list";

function chipClass(b: BookingWithClient): string {
  return `block w-full text-left rounded-md border px-1.5 py-0.5 text-[10px] md:text-[11px] leading-snug truncate ${paymentStatusClass(
    b.payment_status,
    b.booking_status,
    b.due_date
  )}`;
}

const CalendarPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view") as ViewMode | null;
  const [view, setView] = useState<ViewMode>(
    viewParam === "week" || viewParam === "list" || viewParam === "month" ? viewParam : "month"
  );
  const [cursor, setCursor] = useState(() => new Date());
  const [rows, setRows] = useState<BookingWithClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Calendar · Olivia Flemming Admin";
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("view", view);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync view
  }, [view]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const res = await listBookings({ includeCancelled: true });
      if (!alive) return;
      if (res.ok === false) {
        setError(res.error);
        setRows([]);
      } else {
        setError(null);
        setRows(res.data);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const byDate = useMemo(() => {
    const map = new Map<string, BookingWithClient[]>();
    for (const b of rows) {
      if (b.booking_status === "archived") continue;
      const list = map.get(b.performance_date) ?? [];
      list.push(b);
      map.set(b.performance_date, list);
    }
    for (const [, list] of map) {
      list.sort((a, c) => (a.start_time ?? "").localeCompare(c.start_time ?? ""));
    }
    return map;
  }, [rows]);

  const today = useMemo(() => new Date(), []);
  const todayIso = toISODate(today);

  const title =
    view === "month"
      ? formatMonthYear(cursor)
      : view === "week"
        ? formatWeekRange(cursor)
        : "Upcoming list";

  const goPrev = () => {
    if (view === "month") setCursor((c) => addMonths(c, -1));
    else if (view === "week") setCursor((c) => addDays(c, -7));
  };

  const goNext = () => {
    if (view === "month") setCursor((c) => addMonths(c, 1));
    else if (view === "week") setCursor((c) => addDays(c, 7));
  };

  const goToday = () => setCursor(new Date());

  const addHref = (dateIso?: string) =>
    dateIso ? `/admin/bookings/new?date=${dateIso}` : "/admin/bookings/new";

  const listRows = useMemo(() => {
    return rows
      .filter((b) => b.booking_status !== "archived")
      .filter((b) => b.performance_date >= todayIso || b.booking_status === "cancelled")
      .sort((a, b) => {
        const d = a.performance_date.localeCompare(b.performance_date);
        if (d !== 0) return d;
        return (a.start_time ?? "").localeCompare(b.start_time ?? "");
      });
  }, [rows, todayIso]);

  const renderBookingChip = (b: BookingWithClient) => (
    <Link
      key={b.id}
      to={`/admin/bookings/${b.id}`}
      className={chipClass(b)}
      title={`${b.clients?.venue_or_client_name ?? ""} · ${formatTime(b.start_time)}`}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="font-medium">
        {formatTime(b.start_time) !== "—" ? `${formatTime(b.start_time)} ` : ""}
      </span>
      {b.clients?.venue_or_client_name ?? b.reference}
    </Link>
  );

  return (
    <AdminShell
      title="Calendar"
      actions={
        <Link
          to={addHref(toISODate(cursor))}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-black px-5 py-3 text-sm font-medium hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" />
          Add Booking
        </Link>
      }
    >
      {!isSupabaseConfigured && (
        <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100/90 font-inter">
          Supabase is not configured yet — calendar UI works; bookings appear once keys and data are connected.
        </div>
      )}
      {error && (
        <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          {(["month", "week", "list"] as ViewMode[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`rounded-full px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                view === v
                  ? "border-primary bg-primary/15 text-primary"
                  : "border-white/15 text-white/55 hover:border-white/30"
              }`}
            >
              {v}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {view !== "list" && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="p-2 rounded-full border border-white/15 hover:border-primary hover:text-primary"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="p-2 rounded-full border border-white/15 hover:border-primary hover:text-primary"
                  aria-label="Next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={goToday}
              className="rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-widest hover:border-primary hover:text-primary"
            >
              Today
            </button>
          </div>
          <h2 className="font-playfair text-xl md:text-2xl text-white">{title}</h2>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 text-[11px] font-inter text-white/50">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400" /> Paid
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-300" /> Awaiting
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-400" /> Attention
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-white/40" /> Cancelled
          </span>
        </div>
      </div>

      {loading ? (
        <p className="text-white/50 text-sm font-inter">Loading calendar…</p>
      ) : view === "month" ? (
        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-7 bg-white/5 border-b border-white/10">
            {WEEKDAY_LABELS.map((d) => (
              <div
                key={d}
                className="px-1 py-2 text-center text-[10px] md:text-xs uppercase tracking-wider text-white/40 font-inter"
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 auto-rows-fr">
            {monthGrid(cursor).map((day) => {
              const iso = toISODate(day);
              const items = byDate.get(iso) ?? [];
              const inMonth = isSameMonth(day, cursor);
              const isToday = isSameDay(day, today);
              return (
                <div
                  key={iso}
                  className={`min-h-[88px] md:min-h-[110px] border-t border-r border-white/10 p-1 md:p-1.5 flex flex-col gap-1 ${
                    inMonth ? "bg-black/20" : "bg-black/40 opacity-45"
                  }`}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span
                      className={`text-xs font-inter w-6 h-6 flex items-center justify-center rounded-full ${
                        isToday ? "bg-primary text-black font-medium" : "text-white/70"
                      }`}
                    >
                      {day.getDate()}
                    </span>
                    <Link
                      to={addHref(iso)}
                      className="opacity-0 hover:opacity-100 focus:opacity-100 md:opacity-60 p-0.5 text-primary"
                      aria-label={`Add booking on ${iso}`}
                      title="Add booking"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                  <div className="flex flex-col gap-0.5 overflow-hidden">
                    {items.slice(0, 3).map(renderBookingChip)}
                    {items.length > 3 && (
                      <Link
                        to={`/admin/bookings?q=${encodeURIComponent(iso)}`}
                        className="text-[10px] text-white/40 px-1 hover:text-primary"
                      >
                        +{items.length - 3} more
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : view === "week" ? (
        <div className="space-y-3">
          {weekDays(cursor).map((day) => {
            const iso = toISODate(day);
            const items = byDate.get(iso) ?? [];
            const isToday = isSameDay(day, today);
            return (
              <section
                key={iso}
                className={`rounded-2xl border p-4 md:p-5 ${
                  isToday ? "border-primary/40 bg-primary/5" : "border-white/10 bg-secondary/40"
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h3 className="font-playfair text-lg">
                    {formatDisplayDate(iso)}
                    {isToday && (
                      <span className="ml-2 text-xs uppercase tracking-widest text-primary font-inter">
                        Today
                      </span>
                    )}
                  </h3>
                  <Link
                    to={addHref(iso)}
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-primary hover:underline"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </Link>
                </div>
                {items.length === 0 ? (
                  <p className="text-sm text-white/40 font-inter">No bookings</p>
                ) : (
                  <ul className="space-y-2">
                    {items.map((b) => (
                      <li key={b.id}>
                        <Link
                          to={`/admin/bookings/${b.id}`}
                          className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border px-3 py-3 ${paymentStatusClass(
                            b.payment_status,
                            b.booking_status,
                            b.due_date
                          )}`}
                        >
                          <div>
                            <p className="font-inter text-sm text-white">
                              {formatTime(b.start_time)}
                              {b.end_time ? `–${formatTime(b.end_time)}` : ""} ·{" "}
                              {b.clients?.venue_or_client_name ?? "—"}
                            </p>
                            <p className="text-xs text-white/50 mt-0.5">
                              {CATEGORY_LABELS[b.category]} · {b.reference}
                            </p>
                          </div>
                          <span className="text-sm font-inter">
                            {formatGbpFromPence(b.fee_pence)}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 overflow-hidden">
          {listRows.length === 0 ? (
            <p className="p-8 text-center text-white/50 text-sm font-inter">
              No upcoming bookings.{" "}
              <Link to={addHref()} className="text-primary hover:underline">
                Add one
              </Link>
            </p>
          ) : (
            <ul className="divide-y divide-white/10">
              {listRows.map((b) => (
                <li key={b.id}>
                  <Link
                    to={`/admin/bookings/${b.id}`}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-4 hover:bg-white/[0.03]"
                  >
                    <div>
                      <p className="font-playfair text-lg">
                        {b.clients?.venue_or_client_name ?? "—"}
                      </p>
                      <p className="text-sm text-white/50 font-inter mt-1">
                        {formatDisplayDate(b.performance_date)} · {formatTime(b.start_time)} ·{" "}
                        {CATEGORY_LABELS[b.category]} · {b.reference}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm">{formatGbpFromPence(b.fee_pence)}</span>
                      <span
                        className={`rounded-full border px-2.5 py-1 text-[11px] uppercase tracking-wider ${paymentStatusClass(
                          b.payment_status,
                          b.booking_status,
                          b.due_date
                        )}`}
                      >
                        {b.booking_status === "cancelled" ? "Cancelled" : isOverdue(b.payment_status, b.due_date) ? "overdue" : b.payment_status}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Quick jump: week containing today when in month */}
      {view === "month" && (
        <p className="mt-4 text-xs text-white/35 font-inter">
          Week of {formatWeekRange(startOfWeek(cursor))} · tap a day&apos;s + to prefill the date
        </p>
      )}
    </AdminShell>
  );
};

export default CalendarPage;
